/**
 * Pricing band — the #pricing anchor target, set as an engrossed FEE
 * SCHEDULE (the one pricing document every advocate already reads).
 *
 * Visual system: the landing page's chamber-ledger identity — parchment
 * ground, brass hairlines, display serif numerals, IBM Plex overlines,
 * sharp corners. The one bold move per practice row is INK, not a claim:
 * each practice's top tier is a dark chamber-panel card (a left→right
 * crescendo), because we have no sales data to honestly print
 * "Recommended" — the chips on cards state FACTS only ("All 25 High
 * Courts", "Billed annually").
 *
 * Commercial copy rules preserved from the first version:
 * - Prices EXCLUDE GST ("+ GST" on the price itself; advocates claim input
 *   credit and read ex-GST as normal).
 * - AI usage in CREDITS, never rupees (a rupee figure lets any reader
 *   compute gross margin; "250 credits" also means more to an advocate
 *   than "₹280 of AI"). Rupee value stays internal in the catalog.
 * - Matter counts mirror the catalog exactly.
 *
 * Criminal only (2026-10-19): civil and corporate are not on sale until they
 * are complete, so their rows are gone from the schedule (git has them). Put a
 * row back the same day its practiceAreaRoadmap.ts status flips to
 * 'available' — and restore the `cadence` / `annualOnly` card props, which
 * only corporate Firm Max used.
 *
 * The per-feature "What a credit buys" rate card and its "Always free" line
 * are off the page (Ajay, 2026-11-01); the trial docket note now runs the
 * full width under the plans. git has the rate card.
 */

import { ROLE_LABELS } from '../../../types/practiceArea';
import { sectionLabel } from './homepageContent';
import {
  VERTICAL_BY_PRACTICE,
  rememberPlanIntent,
  tierFromPlanName,
} from './planIntent';

interface PricingBandProps {
  onLogin: () => void;
  loading: boolean;
}

interface Plan {
  name: string;
  price: string;
  annual?: string;
  matters: string;
  credits: string;
  /** Documents the advocate uploads; platform-generated PDFs are not counted. */
  storage: string;
  note?: string;
  /** Dark chamber-panel treatment — the practice's top tier. */
  flagship?: boolean;
  /** Factual chip in the card corner. Facts only, never "Recommended". */
  chip?: string;
}

interface Practice {
  numeral: string;
  practice: string;
  tagline: string;
  plans: Plan[];
  includes: string[];
}

const PRACTICES: Practice[] = [
  {
    numeral: 'I',
    practice: ROLE_LABELS.criminal,
    tagline:
      'The heaviest AI in the product — a three-stage defence theory built across the whole file.',
    plans: [
      {
        name: 'Pro',
        price: '1,499',
        annual: '₹14,990 a year',
        matters: '25 active matters',
        credits: '250',
        storage: '10 GB',
        note: '≈ 16 full case theories a month',
      },
      {
        name: 'Max',
        price: '2,999',
        annual: '₹29,990 a year',
        matters: '100 active matters',
        credits: '1,000',
        storage: '25 GB',
        flagship: true,
        chip: 'All 25 High Courts',
        note: 'Full-text search across 1.7 crore High Court judgments',
      },
    ],
    includes: [
      'FIR analysis — sections, bail position, defence strategy',
      'Case theory — 3-stage builder with checkpoints',
      'Bail grounds checklist and precedent authority',
      'Hearing prep briefs, custody and bail clocks',
    ],
  },
];

export default function PricingBand({ onLogin, loading }: PricingBandProps) {
  return (
    <section id="pricing" className="scroll-mt-16 bg-parchment px-5 sm:px-8 lg:px-14 py-20">
      {/* Section head — the fee-schedule conceit is named, then earned. */}
      <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-brass-ink">
        {sectionLabel('pricing')}
      </p>
      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="font-display font-normal text-3xl lg:text-[44px] text-chamber-panel [text-wrap:balance]">
          Criminal practice.{' '}
          <em className="font-editorial italic text-brass-ink">Two plans.</em>
        </h2>
        <p className="font-editorial italic text-[15px] text-chamber-muted-dim max-w-md">
          Built for the criminal bar — FIR to bail to hearing, on the new
          codes.
        </p>
      </div>

      <div className="mt-10 border-t-2 border-chamber-panel">
        {PRACTICES.map((p) => (
          <div
            key={p.practice}
            className="border-b border-brass-hairline py-12 grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-8"
          >
            {/* Practice identity — the numeral is furniture, oversized and
                ghosted so the row is findable at a glance without shouting. */}
            <div className="lg:col-span-4 relative">
              <span
                aria-hidden
                className="font-display text-[96px] leading-[0.8] text-brass/[0.14] select-none absolute -top-2 -left-1"
              >
                {p.numeral}
              </span>
              <div className="relative pt-8 pl-1">
                <h3 className="font-display text-[26px] text-chamber-panel">
                  {p.practice}
                </h3>
                <p className="font-editorial italic text-[15px] text-chamber-muted-dim mt-3">
                  {p.tagline}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {p.includes.map((f) => (
                    <li
                      key={f}
                      className="font-editorial text-[14px] text-chamber-panel leading-snug pl-5 relative"
                    >
                      <span className="absolute left-0 top-[0.62em] w-2.5 h-px bg-brass" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Plan cards — parchment tier, then the dark flagship. */}
            <div
              className={`lg:col-span-8 grid grid-cols-1 gap-4 ${
                p.plans.length > 1 ? 'sm:grid-cols-2' : ''
              }`}
            >
              {p.plans.map((plan) => {
                const dark = !!plan.flagship;
                return (
                  <div
                    key={plan.name}
                    className={
                      dark
                        ? 'relative bg-chamber-panel text-parchment p-6 lg:p-7 flex flex-col shadow-[6px_6px_0_0_rgba(0,0,0,0.06)] transition-transform duration-200 hover:-translate-y-0.5'
                        : 'relative bg-parchment border border-brass-hairline p-6 lg:p-7 flex flex-col transition-colors duration-200 hover:border-brass'
                    }
                  >
                    {/* Brass corner tick — the engrossed-document detail. */}
                    <span
                      aria-hidden
                      className={`absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 ${
                        dark ? 'border-brass' : 'border-brass/50'
                      }`}
                    />

                    <div className="flex items-start justify-between gap-3">
                      <p
                        className={`font-mono text-[10px] tracking-[0.18em] uppercase ${
                          dark ? 'text-brass' : 'text-chamber-muted-dim'
                        }`}
                      >
                        {plan.name}
                      </p>
                      {plan.chip && (
                        <span className="font-mono text-[9px] tracking-[0.12em] uppercase border border-brass/60 text-brass px-2 py-0.5 whitespace-nowrap">
                          {plan.chip}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 flex items-baseline">
                      <span
                        className={`font-display text-[20px] mr-1 ${
                          dark ? 'text-brass' : 'text-brass-ink'
                        }`}
                      >
                        ₹
                      </span>
                      <span
                        className={`font-display text-[44px] leading-none tabular-nums tracking-tight ${
                          dark ? 'text-parchment' : 'text-chamber-panel'
                        }`}
                      >
                        {plan.price}
                      </span>
                    </div>
                    <p
                      className={`font-mono text-[10px] tracking-[0.08em] mt-2 ${
                        dark ? 'text-parchment/60' : 'text-chamber-muted-dim'
                      }`}
                    >
                      + GST / month
                    </p>
                    {plan.annual && (
                      <p
                        className={`font-editorial italic text-[13px] mt-1 ${
                          dark ? 'text-parchment/70' : 'text-chamber-muted-dim'
                        }`}
                      >
                        or {plan.annual} + GST — two months free
                      </p>
                    )}

                    {/* Fee-schedule rows with dotted leaders. */}
                    <dl className="mt-6 flex-1 space-y-2.5">
                      {(
                        [
                          ['Matters', plan.matters],
                          ['AI credits', `${plan.credits} / mo`],
                          ['Document storage', plan.storage],
                        ] as [string, string][]
                      ).map(([dt, dd]) => (
                        <div key={dt} className="flex items-baseline gap-2">
                          <dt
                            className={`font-editorial text-[14px] ${
                              dark ? 'text-parchment' : 'text-chamber-panel'
                            }`}
                          >
                            {dt}
                          </dt>
                          <span
                            aria-hidden
                            className={`flex-1 border-b border-dotted translate-y-[-3px] ${
                              dark ? 'border-parchment/25' : 'border-brass/40'
                            }`}
                          />
                          <dd
                            className={`font-mono text-[11px] tabular-nums whitespace-nowrap ${
                              dark ? 'text-parchment/80' : 'text-chamber-muted-dim'
                            }`}
                          >
                            {dd}
                          </dd>
                        </div>
                      ))}
                      {plan.note && (
                        <p
                          className={`font-editorial italic text-[13px] pt-1.5 leading-snug ${
                            dark ? 'text-brass' : 'text-brass-ink'
                          }`}
                        >
                          {plan.note}
                        </p>
                      )}
                    </dl>

                    <button
                      type="button"
                      onClick={() => {
                        // Remember WHICH plan before the popup. Every card used
                        // to fire a bare onLogin, so the choice was discarded at
                        // the auth boundary while the aria-label still announced
                        // it — the button promised what the system did not do.
                        const vertical = VERTICAL_BY_PRACTICE[p.practice];
                        if (vertical) {
                          rememberPlanIntent({
                            vertical,
                            tier: tierFromPlanName(plan.name),
                          });
                        }
                        onLogin();
                      }}
                      disabled={loading}
                      aria-label={`Subscribe to ${p.practice} ${plan.name} — ₹${plan.price} a month`}
                      className={
                        dark
                          ? 'mt-6 bg-brass text-chamber-ink px-5 py-3 text-[12px] font-semibold tracking-[0.1em] hover:bg-parchment transition-colors disabled:opacity-50 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2'
                          : 'mt-6 border border-brass text-brass-ink px-5 py-3 text-[12px] font-semibold tracking-[0.1em] hover:bg-brass hover:text-chamber-ink transition-colors disabled:opacity-50 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2'
                      }
                    >
                      SUBSCRIBE — ₹{plan.price}/mo
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Trial terms as a docket note — the strongest trust copy on the page
          was previously buried in a closing paragraph. Full width under the
          plans, its four terms in two columns from md. */}
      <div className="mt-14 border border-brass/60 relative p-7 lg:p-8">
        <span
          aria-hidden
          className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-brass"
        />
        <span
          aria-hidden
          className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-brass"
        />
        <h3 className="font-mono text-[11px] tracking-[0.18em] uppercase text-brass-ink">
          The trial, in plain terms
        </h3>
        <ul className="mt-5 grid gap-x-12 gap-y-3 md:grid-cols-2">
          {[
            // "in full" is now literally true: a trial is granted the SAME
            // credit allowance the vertical's Pro plan sells (250 for
            // criminal), so converting takes nothing away. It previously
            // granted no metered allowance at all.
            // Storage is the one exception (owner decision 2026-10-30: 1 GB
            // on the trial, 10 GB on Pro), so the line says so.
            '30 days on the Pro plan — the same matters and AI credits, nothing held back except storage: 1 GB during the trial.',
            // Was: "The clock starts with your first matter, not the day you
            // sign up." No such clock existed, and a first-matter anchor made
            // the trial open-ended — research and every analyzer run with
            // zero matters, so a lawyer who never made one never started the
            // clock. Now anchored at signup, and the app says so.
            'No card. 30 days from the day you sign up.',
            'A tax invoice carrying your GSTIN is issued on every payment.',
            // Matches what the product actually does at expiry: reads and
            // exports keep working, writes and AI stop (require_write_access).
            'When it ends, your matters, documents and diary stay readable and exportable.',
          ].map((line) => (
            <li
              key={line}
              className="font-editorial text-[15px] text-chamber-panel leading-snug pl-5 relative"
            >
              <span className="absolute left-0 top-[0.62em] w-2.5 h-px bg-brass" />
              {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
